/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { Moment } from 'moment';
import { AccountService } from 'app/core';
import { Calendar, CalendarEvent, CalendarSettings, CalendarWeekday } from 'app/modules/calendar/calendar.types';

export const CALENDAR_SETTINGS: CalendarSettings = {
  dateFormat: 'll', // Aug 20, 2019
  timeFormat: '24', // 24-hour format
  startWeekOn: 1, // Monday
};
export const CALENDAR_WEEKDAYS: CalendarWeekday[] = [
  {
    abbr: 'M',
    label: 'Monday',
    value: 'MO',
  },
  {
    abbr: 'T',
    label: 'Tuesday',
    value: 'TU',
  },
  {
    abbr: 'W',
    label: 'Wednesday',
    value: 'WE',
  },
  {
    abbr: 'T',
    label: 'Thursday',
    value: 'TH',
  },
  {
    abbr: 'F',
    label: 'Friday',
    value: 'FR',
  },
  {
    abbr: 'S',
    label: 'Saturday',
    value: 'SA',
  },
  {
    abbr: 'S',
    label: 'Sunday',
    value: 'SU',
  },
];

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private _events: BehaviorSubject<CalendarEvent[] | null>;
  private _loadedEventsRange: { start: Moment | null; end: Moment | null };
  private readonly _numberOfDaysToPrefetch = 60;
  private _settings: BehaviorSubject<CalendarSettings | null>;
  private _accountId: string;

  constructor(
    private _httpClient: HttpClient,
    private _accountService: AccountService,
  ) {
    this._events = new BehaviorSubject(null);
    this._loadedEventsRange = {
      start: null,
      end: null,
    };
    this._settings = new BehaviorSubject(null);
    this._accountId = this._accountService.getCurrentAccount().id;
  }

  /**
   * Getter for events
   */
  get events$(): Observable<CalendarEvent[]> {
    return this._events.asObservable();
  }

  /**
   * Getter for settings
   */
  get settings$(): Observable<CalendarSettings> {
    return this._settings.asObservable();
  }

  /**
   * Setter for accountId
   */
  set accountId(val: string) {
    this._accountId = val;
  }

  /**
   * Get calendars
   */
  getCalendars(): Observable<Calendar[]> {
    return this._httpClient.get<Calendar[]>(`accounts/${this._accountId}/calendar`);
  }

  /**
   * Get events
   */
  getEvents(start: Moment, end: Moment, replace = true, filters: any = {}): Observable<CalendarEvent[]> {
    const { objectTypeIn = [], assignedUserIdIn = [], relatedToType = null, relatedToId = null } = filters;

    // Set the new start date for loaded events
    if (replace || !this._loadedEventsRange.start || start.isBefore(this._loadedEventsRange.start)) {
      this._loadedEventsRange.start = start;
    }

    // Set the new end date for loaded events
    if (replace || !this._loadedEventsRange.end || end.isAfter(this._loadedEventsRange.end)) {
      this._loadedEventsRange.end = end;
    }
    let params = {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
      assigned_user_id_in: assignedUserIdIn.join(','),
      object_type_in: objectTypeIn.join(','),
    };

    if (relatedToId) {
      params['related_to_id'] = relatedToId;
    }
    if (relatedToType) {
      params['related_to_type'] = relatedToType;
    }
    // Get the events
    return this._httpClient
      .get<CalendarEvent[]>(`accounts/${this._accountId}/calendar`, {
        params: params,
      })
      .pipe(
        switchMap((response) =>
          this._events.pipe(
            take(1),
            map((events) => {
              // If replace...
              if (replace) {
                // Execute the observable with the response replacing the events object
                this._events.next(response);
              }
              // Otherwise...
              else {
                // If events is null, replace it with an empty array
                events = events || [];

                // Execute the observable by appending the response to the current events
                this._events.next([...events, ...response]);
              }

              // Return the response
              return response;
            }),
          ),
        ),
      );
  }

  /**
   * Prefetch future events
   */
  prefetchFutureEvents(end: Moment): Observable<CalendarEvent[]> {
    // Calculate the remaining prefetched days
    const remainingDays = this._loadedEventsRange.end.diff(end, 'days');

    // Return, if remaining days is bigger than the number
    // of days to prefetch. This means we were already been
    // there and fetched the events data so no need for doing
    // it again.
    if (remainingDays >= this._numberOfDaysToPrefetch) {
      return of([]);
    }

    // Figure out the start and end dates
    const start = this._loadedEventsRange.end.clone().add(1, 'day');
    end = this._loadedEventsRange.end.clone().add(this._numberOfDaysToPrefetch - remainingDays, 'days');

    // Prefetch the events
    return this.getEvents(start, end);
  }

  /**
   * Prefetch past events
   */
  prefetchPastEvents(start: Moment): Observable<CalendarEvent[]> {
    // Calculate the remaining prefetched days
    const remainingDays = start.diff(this._loadedEventsRange.start, 'days');

    // Return, if remaining days is bigger than the number
    // of days to prefetch. This means we were already been
    // there and fetched the events data so no need for doing
    // it again.
    if (remainingDays >= this._numberOfDaysToPrefetch) {
      return of([]);
    }

    // Figure out the start and end dates
    start = this._loadedEventsRange.start.clone().subtract(this._numberOfDaysToPrefetch - remainingDays, 'days');
    const end = this._loadedEventsRange.start.clone().subtract(1, 'day');

    // Prefetch the events
    return this.getEvents(start, end);
  }

  /**
   * Get settings
   * https://github.com/RealtyClub/termsheet-frontend/pull/1782/files#diff-ab4001a9e3553d43a6d228aa34f3f5d8c5ec0fff4bb8090e3184a204e4466e3cL530
   */
  getSettings(): Observable<CalendarSettings> {
    this._settings.next(CALENDAR_SETTINGS);
    return of(CALENDAR_SETTINGS);
  }

  /**
   * Get weekdays
   */
  getWeekdays(): Observable<CalendarWeekday[]> {
    return of(CALENDAR_WEEKDAYS);
  }
}
